import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Users2, Cake, Link2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 flex items-center gap-3">
    <Icon className="w-5 h-5 text-amber-600" />
    <div><div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div><div className="text-xl font-bold text-slate-900" data-testid={testid}>{value}</div></div>
  </div>
);

const List = ({ title, hint, items, q, to, testid }) => {
  const s = q.trim().toLowerCase();
  const shown = items.filter((p) => !s || p.name.toLowerCase().includes(s));
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={testid}>
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-baseline justify-between"><span className="font-semibold text-slate-900">{title}</span><span className="text-sm font-bold text-amber-700" data-testid={`${testid}-count`}>{items.length}</span></div>
        <div className="text-xs text-slate-500 mt-0.5">{hint} · <Link to={to} className="underline text-amber-700">open</Link></div>
      </div>
      <div className="max-h-[55vh] overflow-y-auto divide-y divide-slate-100">
        {shown.length === 0 && <p className="p-6 text-center text-sm text-slate-500">{items.length ? "No matches." : "Nobody missing."}</p>}
        {shown.map((p) => <div key={p.row_id} className="px-4 py-2.5 text-sm text-slate-800" data-testid={`${testid}-item-${p.row_id}`}>{p.name}</div>)}
      </div>
    </div>
  );
};

export const CrossCheck = ({ refreshKey }) => {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/birthdays/crosscheck").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [refreshKey]);
  if (!d) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (!d.census_ready || !d.register_ready) return (
    <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-600" data-testid="crosscheck-not-ready">
      Import both lists first: {!d.census_ready && <Link to="/census" className="underline text-amber-700">Frigate Census</Link>}{!d.census_ready && !d.register_ready && " and "}{!d.register_ready && "the Birthday Register (Register tab)"}.
    </div>
  );
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Stat icon={Users2} label="Active in census" value={d.census_total} testid="crosscheck-census-total" />
        <Stat icon={Cake} label="Active in register" value={d.register_total} testid="crosscheck-register-total" />
        <Stat icon={Link2} label="On both lists" value={d.matched} testid="crosscheck-matched" />
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a name…" className="bg-white h-11 mb-3 max-w-md" data-testid="crosscheck-search" />
      <p className="text-xs text-slate-500 mb-3">Names are matched ignoring case, punctuation and word order. Only active entries are compared.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <List title="In census, missing from birthday register" hint="Add their birthdays to the register" items={d.census_only} q={q} to="/birthdays" testid="crosscheck-census-only" />
        <List title="In birthday register, missing from census" hint="Check the census or the spelling" items={d.register_only} q={q} to="/census" testid="crosscheck-register-only" />
      </div>
    </div>
  );
};
